import { Link } from 'react-router-dom';
import type { ComponentType, SVGProps } from 'react';
import { Flame, Clock, Moon, UserX, ChevronRight } from 'lucide-react';
import type { TodayAttention } from '../../types';

type IconC = ComponentType<SVGProps<SVGSVGElement>>;

function AttentionTile({
  to,
  Icon,
  iconClass,
  count,
  label,
}: {
  to: string;
  Icon: IconC;
  iconClass: string;
  count: number;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="card p-3 sm:p-4 flex flex-col items-center text-center gap-1 hover:shadow-md transition-shadow"
    >
      <Icon className={`h-5 w-5 ${iconClass}`} />
      <span className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{count}</span>
      <span className="text-[11px] leading-tight text-gray-500 dark:text-gray-400">{label}</span>
    </Link>
  );
}

/**
 * Three tappable per-rep counters that deep-link into the matching People
 * segment (scoped to "Mine"). Every number is a doorway.
 */
export function NeedsAttention({ attention }: { attention: TodayAttention }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
        Needs attention
      </h3>
      {attention.coachChangeReports > 0 && (
        <Link
          to={
            attention.coachChangeReports === 1 && attention.coachChangeSoloPersonId
              ? `/people/${attention.coachChangeSoloPersonId}?coachFlag=1`
              : '/coach-change-reports'
          }
          className="card mb-3 p-3 sm:p-4 flex items-center gap-3 border-amber-300 dark:border-amber-700 hover:shadow-md transition-shadow"
        >
          <UserX className="h-5 w-5 flex-shrink-0 text-amber-500 dark:text-amber-400" />
          <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">
            <span className="font-semibold tabular-nums">{attention.coachChangeReports}</span>{' '}
            coach change {attention.coachChangeReports === 1 ? 'report' : 'reports'} to review
          </span>
          <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
        </Link>
      )}
      <div className="grid grid-cols-3 gap-3">
        <AttentionTile
          to="/people?scope=mine&segment=hot"
          Icon={Flame}
          iconClass="text-red-500 dark:text-red-400"
          count={attention.hot}
          label="Hot"
        />
        <AttentionTile
          to="/people?scope=mine&segment=followUpDue"
          Icon={Clock}
          iconClass="text-amber-500 dark:text-amber-400"
          count={attention.followUpDue}
          label="Follow-ups due"
        />
        <AttentionTile
          to="/people?scope=mine&segment=noActivity30d"
          Icon={Moon}
          iconClass="text-slate-400 dark:text-slate-500"
          count={attention.atRisk}
          label="At risk"
        />
      </div>
    </div>
  );
}
